import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers, Plus, Search, Copy, Files, Award, Paperclip, Trash2, Download, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/utils/formatINR";

// Prominent outcome pill for closed assemblies (mirrors the NPD list).
const ASM_OUTCOME_BADGE = {
  won:               { label: "Won",     cls: "bg-emerald-100 text-emerald-700", title: "Marked Won" },
  lost:              { label: "Lost",    cls: "bg-red-100 text-red-700",         title: "Marked Lost" },
  dropped:           { label: "Dropped", cls: "bg-stone-200 text-stone-700",     title: "Marked Dropped" },
  partially_awarded: { label: "Partial", cls: "bg-purple-100 text-purple-700",   title: "Partially awarded" },
};

// Approval-state pill: Approved / Under Approval / Draft (shown when the assembly isn't closed).
const asmApprovalBadge = (a) => {
  if (a.approved_for_export) return { label: "Approved", cls: "bg-emerald-100 text-emerald-700", title: "Approved for export" };
  const st = String(a.status || "").toLowerCase();
  if (st && st !== "draft") return { label: "Under Approval", cls: "bg-amber-100 text-amber-800", title: "Awaiting manager approval" };
  return { label: "Draft", cls: "bg-slate-100 text-slate-600", title: "Not yet sent for approval" };
};

// Round/stage pill (mirrors NPD list): Draft until sent, then Sent.
const asmStage = (a) => (String(a.status || "").toLowerCase() && String(a.status).toLowerCase() !== "draft") ? "Sent" : "Draft";
const ASM_STAGE_CLS = { Draft: "bg-slate-100 text-slate-700", Sent: "bg-sky-100 text-sky-700" };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

export default function AssemblyList() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [custFilter, setCustFilter] = useState("");
  const [custOpen, setCustOpen] = useState(false);   // searchable customer picker (same as NPD Costings)
  const [custQuery, setCustQuery] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const newMenuRef = useRef(null);

  useEffect(() => {
    if (!newMenuOpen) return;
    const onDown = (e) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target)) {
        setNewMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [newMenuOpen]);
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.search = q;
      if (custFilter) params.customer_id = custFilter;
      if (isAdmin && unitFilter) params.unit_id = unitFilter;
      if (outcomeFilter) params.outcome = outcomeFilter;
      if (statusFilter) params.status = statusFilter;
      if (projectFilter === "__standalone__") params.standalone_only = true;
      else if (projectFilter) params.project_id = projectFilter;
      const r = await api.get("/assemblies", { params });
      setRows(r.data.assemblies || []);
    } finally { setLoading(false); }
  };

  const cloneRow = async (asm) => {
    if (!window.confirm(`Clone ${asm.assembly_number} (${asm.assembly_name})? A new draft assembly will be created.`)) return;
    try {
      const r = await api.post(`/assemblies/${asm.assembly_id}/clone`);
      toast.success(`Cloned → ${r.data.assembly_number}`);
      nav(`/assemblies/${r.data.assembly_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Clone failed"); }
  };

  // One-click "Make a copy" — duplicate the assembly + all its sub-parts, stay on the list.
  const copyRow = async (asm) => {
    if (!window.confirm(`Make a copy of ${asm.assembly_number} (${asm.assembly_name})? A new draft assembly with all ${(asm.components || []).length} sub-part(s) will be created.`)) return;
    try {
      const r = await api.post(`/assemblies/${asm.assembly_id}/clone`);
      toast.success(`Copied → ${r.data.assembly_number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Copy failed"); }
  };

  const deleteRow = async (asm) => {
    if (!window.confirm(`Soft-delete ${asm.assembly_number} (${asm.assembly_name})?\n\nThe record stays in the database and admins can restore it from the Trash page. If this assembly is the only live costing linked to its RFQ Register entry, that RFQ will also be cascade-deleted.`)) return;
    try {
      await api.delete(`/assemblies/${asm.assembly_id}`);
      toast.success(`Moved ${asm.assembly_number} to Trash`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data.projects || [])).catch(() => {});
    if (isAdmin) api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250); return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, custFilter, unitFilter, outcomeFilter, statusFilter, projectFilter]);

  const customerName = (id) => customers.find((c) => c.customer_id === id)?.name || "—";

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="assembly-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Layers size={22} className="text-orange-600" /> Assemblies
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Multi-part finished items built from existing NPDs plus assembly-level costs.</p>
        </div>
        <button
          type="button"
          onClick={async () => {
            const params = new URLSearchParams();
            if (q) params.set("search", q);
            if (custFilter) params.set("customer_id", custFilter);
            if (unitFilter && user?.role === "admin") params.set("unit_id", unitFilter);
            if (outcomeFilter) params.set("outcome", outcomeFilter);
            if (statusFilter) params.set("status", statusFilter);
            if (projectFilter && projectFilter !== "__standalone__") params.set("project_id", projectFilter);
            else if (projectFilter === "__standalone__") params.set("standalone_only", "true");
            const res = await api.get(`/assemblies/_/export.xlsx?${params.toString()}`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = (res.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "AAMPL_Assemblies.xlsx";
            link.click();
            URL.revokeObjectURL(link.href);
          }}
          className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium rounded-md px-3 py-2 mr-2"
          data-testid="assembly-export-btn"
        >
          <Download size={14} /> Export Excel
        </button>
        <Link to="/assemblies/compare" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium rounded-md px-3 py-2 mr-2" data-testid="assembly-compare-cross">
          <Layers size={14} /> Compare Two
        </Link>
        <div className="relative inline-block" ref={newMenuRef}>
          <button
            onClick={() => setNewMenuOpen((o) => !o)}
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-4 py-2"
            data-testid="assembly-new"
          >
            <Plus size={16} /> New Assembly <ChevronDown size={14} className={`transition ${newMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {newMenuOpen && (
            <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1" data-testid="assembly-new-menu">
              <button
                onClick={() => { setNewMenuOpen(false); nav("/assemblies/new?proto=1"); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
                data-testid="assembly-new-proto"
              >
                <Layers size={16} className="mt-0.5 text-orange-600" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Proto Assembly</div>
                  <div className="text-xs text-slate-500">Prototype — reduced sections</div>
                </div>
              </button>
              <button
                onClick={() => { setNewMenuOpen(false); nav("/assemblies/new"); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2 border-t border-slate-100"
                data-testid="assembly-new-series"
              >
                <Layers size={16} className="mt-0.5 text-orange-600" />
                <div>
                  <div className="text-sm font-medium text-slate-900">Series Assembly</div>
                  <div className="text-xs text-slate-500">Full production costing</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className={`p-4 border-b border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-3 ${isAdmin ? "lg:grid-cols-6" : "lg:grid-cols-5"}`}>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assembly, ASM#…"
                   className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="asm-search" />
          </div>
          {/* Searchable customer picker — same simple combobox as NPD Costings. */}
          <div className="relative" data-testid="asm-customer-filter">
            <input
              type="text"
              value={custOpen ? custQuery : (customers.find((c) => c.customer_id === custFilter)?.name || "")}
              onChange={(e) => { setCustQuery(e.target.value); setCustOpen(true); }}
              onFocus={() => { setCustQuery(""); setCustOpen(true); }}
              onBlur={() => setTimeout(() => setCustOpen(false), 150)}
              placeholder="All customers"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
              data-testid="asm-customer-filter-input"
            />
            {custOpen && (
              <ul className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg text-sm" data-testid="asm-customer-filter-list">
                <li
                  onMouseDown={(e) => { e.preventDefault(); setCustFilter(""); setCustOpen(false); }}
                  className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-slate-500 italic border-b border-slate-100"
                  data-testid="asm-customer-filter-all"
                >
                  All customers
                </li>
                {(() => {
                  const query = custQuery.trim().toLowerCase();
                  const list = query
                    ? customers.filter((c) => (c.name || "").toLowerCase().includes(query))
                    : customers;
                  if (list.length === 0) return <li className="px-3 py-2 text-slate-400 italic">No matches</li>;
                  return list.map((c) => (
                    <li
                      key={c.customer_id}
                      onMouseDown={(e) => { e.preventDefault(); setCustFilter(c.customer_id); setCustOpen(false); }}
                      className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer text-slate-800"
                      data-testid="asm-customer-filter-option"
                    >
                      {c.name}
                    </li>
                  ));
                })()}
              </ul>
            )}
          </div>
          {isAdmin && (
            <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              <option value="">All units</option>
              {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.unit_name}</option>)}
            </select>
          )}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="asm-status-filter">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="n1">Round N1</option>
            <option value="n2">Round N2</option>
            <option value="n3">Round N3</option>
            <option value="n4">Round N4</option>
            <option value="n5">Round N5</option>
          </select>
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="asm-project-filter">
            <option value="">All projects + standalone</option>
            <option value="__standalone__">Standalone only (no project)</option>
            <option disabled>──────────────</option>
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.project_number} — {p.project_name}
              </option>
            ))}
          </select>
          <select value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="asm-outcome-filter">
            <option value="">All outcomes</option>
            <option value="pending">Pending</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="dropped">Dropped</option>
          </select>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">
            No assemblies yet. <Link to="/assemblies/new" className="text-orange-600 hover:text-orange-700">Create one →</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3">ASM #</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Assembly</th>
                <th className="text-left p-3">Customer</th>
                {isAdmin && <th className="text-left p-3">Unit</th>}
                <th className="text-left p-3">Status</th>
                <th className="text-center p-3">Sub-parts</th>
                <th className="text-right p-3">Grand Total</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((a) => (
                <tr key={a.assembly_id} className="hover:bg-slate-50">
                  <td className="p-3 font-mono text-xs text-slate-600">
                    <div>{a.assembly_number}</div>
                    {(a.misc_files || []).length > 0 && (
                      <div className="flex items-center gap-1 text-slate-500 text-[10px] mt-0.5" title={`${a.misc_files.length} attachment${a.misc_files.length > 1 ? "s" : ""}`} data-testid={`asm-attachments-${a.assembly_number}`}>
                        <Paperclip size={11} /> {a.misc_files.length}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-500 whitespace-nowrap" title={a.approved_for_export && a.approved_at ? `Approved ${fmtDate(a.approved_at)}` : "Created"}>{fmtDate(a.created_at)}</td>
                  <td className="p-3">
                    <div className="font-medium text-slate-900 truncate max-w-xs">{a.assembly_name}</div>
                    <div className="text-xs text-slate-500">{a.part_number}</div>
                  </td>
                  <td className="p-3 text-slate-700">{customerName(a.customer_id)}</td>
                  {isAdmin && (
                    <td className="p-3">
                      <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                        {(units.find((u) => u.unit_id === a.unit_id) || {}).unit_code || "—"}
                      </span>
                    </td>
                  )}
                  <td className="p-3">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${ASM_STAGE_CLS[asmStage(a)]}`}>{asmStage(a)}</span>
                      {ASM_OUTCOME_BADGE[a.outcome] ? (
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${ASM_OUTCOME_BADGE[a.outcome].cls}`} title={ASM_OUTCOME_BADGE[a.outcome].title} data-testid={`asm-outcome-badge-${a.assembly_number}`}>
                          {a.outcome === "won" && <Award size={11} />}{ASM_OUTCOME_BADGE[a.outcome].label}
                        </span>
                      ) : (() => {
                        const b = asmApprovalBadge(a);
                        return <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${b.cls}`} title={b.title} data-testid={`asm-status-${a.assembly_number}`}>{b.label}</span>;
                      })()}
                    </div>
                  </td>
                  <td className="p-3 text-center font-mono text-sm">{(a.components || []).length}</td>
                  <td className="p-3 text-right font-mono">{formatINR(a.grand_total)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button type="button" onClick={() => copyRow(a)}
                              className="text-slate-400 hover:text-emerald-600 transition"
                              title="Make a copy (one click — new draft, all sub-parts)"
                              data-testid={`asm-copy-${a.assembly_id}`}>
                        <Files size={15} />
                      </button>
                      <button type="button" onClick={() => cloneRow(a)}
                              className="text-slate-400 hover:text-orange-600 transition"
                              title="Clone & open (create a draft copy and jump into it)"
                              data-testid={`asm-clone-${a.assembly_id}`}>
                        <Copy size={15} />
                      </button>
                      {user?.role === "admin" && (
                        <button type="button" onClick={() => deleteRow(a)}
                                className="text-slate-400 hover:text-red-600 transition"
                                title="Soft-delete (admin only)"
                                data-testid={`asm-delete-${a.assembly_id}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                      <Link to={`/assemblies/${a.assembly_id}`} className="text-orange-600 hover:text-orange-700 text-sm font-medium">
                        Open →
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
